import { useEffect, useState } from "react";
import { Platform } from "react-native";

export type GeoSearchBias = {
  lat: number;
  lon: number;
} | null;

const CACHE_KEY = "ferinacura.geo_search_bias";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 Minuten

type CachedBias = { lat: number; lon: number; at: number };

function readCache(): GeoSearchBias {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBias;
    if (Date.now() - parsed.at > MAX_AGE_MS) return null;
    if (!Number.isFinite(parsed.lat) || !Number.isFinite(parsed.lon)) return null;
    return { lat: parsed.lat, lon: parsed.lon };
  } catch {
    return null;
  }
}

function writeCache(bias: GeoSearchBias) {
  if (Platform.OS !== "web" || typeof window === "undefined" || !bias) return;
  try {
    const payload: CachedBias = { ...bias, at: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Ungefähre Position für Fundort-Suche (Web: Browser-Standort, einmal pro Session).
 * Ergebnisse in der Nähe (z. B. Chemnitz statt Hamburg) werden in der DB bevorzugt.
 */
export function useGeoSearchBias(): GeoSearchBias {
  const [bias, setBias] = useState<GeoSearchBias>(() => readCache());

  useEffect(() => {
    if (bias) return;
    if (Platform.OS !== "web" || typeof navigator === "undefined" || !navigator.geolocation) {
      return;
    }

    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const next = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        writeCache(next);
        setBias(next);
      },
      () => {
        /* Nutzer lehnt ab oder Standort nicht verfügbar – Suche ohne Nähe-Bias */
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: MAX_AGE_MS },
    );

    return () => {
      cancelled = true;
    };
  }, [bias]);

  return bias;
}
